import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, tryRefresh } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // status: "loading" | "authenticated" | "anonymous"
  const [state, setState] = useState({ status: "loading", user: null, organization: null, role: null });

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setState({ status: "authenticated", ...data });
      return data;
    } catch (err) {
      if (err?.response?.status === 401) {
        const refreshed = await tryRefresh();
        if (refreshed) {
          try {
            const { data } = await api.get("/auth/me");
            setState({ status: "authenticated", ...data });
            return data;
          } catch { /* fall through */ }
        }
      }
      setState({ status: "anonymous", user: null, organization: null, role: null });
      return null;
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  const login = async (body) => {
    const { data } = await api.post("/auth/login", body);
    setState({ status: "authenticated", user: data.user, organization: data.organization, role: "admin" });
    await loadMe();
    return data;
  };

  const register = async (body) => {
    const { data } = await api.post("/auth/register", body);
    setState({ status: "authenticated", user: data.user, organization: data.organization, role: "owner" });
    await loadMe();
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setState({ status: "anonymous", user: null, organization: null, role: null });
  };

  const switchOrg = async (organizationId) => {
    await api.post("/auth/switch", { organizationId });
    await loadMe();
  };

  return (
    <AuthContext.Provider value={{ ...state, loadMe, login, register, logout, switchOrg, setState }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
